<script setup lang="ts">
import { EVENT_MEDIA_SUB_TYPE, EVENT_MEDIA_TYPE, EVENT_TYPE } from "~/constants";

export interface ListVideosProps {
  items: IModelMediaEvent[];
  title?: string;
  type?: "clip" | "player" | "highlight";
  loading?: boolean;
  hasShowMoreButton?: boolean;
  limit?: number;
  hideOnNoContent?: boolean;
  isPlaying?: boolean;
}

const props = defineProps<ListVideosProps>();

const { t } = useI18n();
const route = useRoute();
const showMore = ref(false);

const displayedItems = computed(() => {
  return showMore.value ? props.items : props.items.slice(0, props.limit);
});

const badgeTitle = (item: IModelMediaEvent) => {
  if (item.mediaType === EVENT_MEDIA_TYPE.USER && item.mediaSubType === EVENT_MEDIA_SUB_TYPE.CLIP) {
    return t("labels.clip", 1);
  }
  else if ((item.mediaType === EVENT_MEDIA_TYPE.USER && item.mediaSubType === EVENT_MEDIA_SUB_TYPE.HIGHLIGHT)
    || (item.mediaType === EVENT_MEDIA_TYPE.GAME && item.mediaSubType === EVENT_MEDIA_SUB_TYPE.AUTO)) {
    return t("labels.highlights", 0);
  }
  else if (item.mediaType === EVENT_MEDIA_TYPE.GAME && item.mediaSubType === EVENT_MEDIA_SUB_TYPE.RECAP) {
    return t("labels.game_recap");
  }
  else if (item.mediaType === EVENT_MEDIA_TYPE.GAME && item.mediaSubType === EVENT_MEDIA_SUB_TYPE.CONDENSED) {
    return t("labels.play_by_play");
  }

  return "";
};
const badgeIcon = (type: TEventType) => type === EVENT_TYPE.USER_CLIP ? "clip" : "highlight";
const gridClass = computed(() => props.type === "player"
  ? "grid gap-4 grid-cols-3 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 px-4"
  : "form-column !px-0");
</script>

<template>
  <BaseList
    :grid-class="gridClass"
    :items="displayedItems"
    :loading="props.loading"
    :limit="props.limit"
    :hide-on-no-content="props.hideOnNoContent"
  >
    <template #start>
      <div v-if="props.title" class="flex items-center justify-between p-4">
        <h3 class="text-heading-4 text-secondary">
          {{ props.title }}
        </h3>
      </div>
    </template>

    <template #item="{ item }">
      <BaseButton
        v-if="props.type === 'player'"
        type="icon"
        tag="nuxt-link"
        :to="{ path: route.path, query: { type: item.eventType, mediaId: item.id } }"
        :name="`item-${item.id}`"
      >
        <CardPlayerHighlight
          :image-url="item.imageUrl"
          :player-number="item.playerNumber"
          :player-color="item.playerColor"
          :team-logo="item.teamLogo"
          :team-title="item.teamTitle"
          :is-active="Boolean(item.id === route.query.mediaId)"
          :is-playing="Boolean((item.id === route.query.mediaId) && props.isPlaying)"
        />
      </BaseButton>

      <BaseButton
        v-else
        type="icon"
        tag="nuxt-link"
        :to="{ path: route.path, query: { type: item.eventType, mediaId: item.id } }"
        :name="`item-${item.id}`"
        class="w-full"
      >
        <CardVideo
          :title="item.title"
          :badge-title="badgeTitle(item)"
          :badge-icon="badgeIcon(item.eventType)"
          :image-url="item.imageUrl"
          :is-active="Boolean(item.id === route.query.mediaId)"
          :is-playing="Boolean((item.id === route.query.mediaId) && props.isPlaying)"
        />
      </BaseButton>
    </template>
    <template #skeleton>
      <CardPlayerHighlightSkeleton v-if="props.type === 'player'" />
      <CardVideoSkeleton v-else />
    </template>
    <template #end>
      <div v-if="props.hasShowMoreButton && props.items.length > (props.limit || props.items.length)" class="mt-4 flex justify-center">
        <BaseButton
          :disabled="props.loading"
          name="load-more"
          link
          type="primary"
          class="text-heading-4 mx-8 truncate font-medium"
          @click="showMore = !showMore"
        >
          {{ showMore ? 'Show less' : 'Show more' }}
          <IconChevronUp v-if="showMore" class="inline" :height="8" :width="8" />
          <IconChevronDown v-else class="inline" :height="8" :width="8" />
        </BaseButton>
      </div>
      <div
        role="separator"
        class="hr mx-4 mt-4"
        style="background: var(--base-background-color)"
      />
    </template>
  </BaseList>
</template>
